import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StatusBarContext } from "./statusBarContext";
import { DEFAULT_STATUS_BAR_COLOR, SYSTEM_NAV_COLOR } from "./statusBarConstants";

const canUseDOM = typeof window !== "undefined" && typeof document !== "undefined";

function ensureRuntimeThemeColorMeta() {
  if (!canUseDOM) return null;
  let meta = document.querySelector('meta[name="theme-color"][data-runtime-theme-color="true"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    meta.setAttribute("data-runtime-theme-color", "true");
    document.head.appendChild(meta);
  }
  return meta;
}

function applyStatusBarColor(color) {
  if (!canUseDOM || !color) return;
  document.documentElement.style.setProperty("--status-bar-color", color);
}

function applyThemeColor(color) {
  if (!canUseDOM || !color) return;
  const meta = ensureRuntimeThemeColorMeta();
  if (meta && meta.getAttribute("content") !== color) {
    meta.setAttribute("content", color);
  }
}

export function StatusBarProvider({ children, defaultColor = DEFAULT_STATUS_BAR_COLOR }) {
  const [layers, setLayers] = useState(() => new Map());
  const lastAppliedRef = useRef(null);

  const register = useCallback((layer) => {
    setLayers((prev) => {
      const next = new Map(prev);
      next.set(layer.id, layer);
      return next;
    });
  }, []);

  const unregister = useCallback((id) => {
    setLayers((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const activeLayer = useMemo(() => {
    let winner = null;
    for (const layer of layers.values()) {
      if (!winner || layer.priority > winner.priority) {
        winner = layer;
      }
    }
    return winner;
  }, [layers]);

  useEffect(() => {
    const color = activeLayer?.color || defaultColor;
    if (color && color !== lastAppliedRef.current) {
      lastAppliedRef.current = color;
      applyStatusBarColor(color);
    }
  }, [activeLayer, defaultColor]);

  useEffect(() => {
    applyThemeColor(SYSTEM_NAV_COLOR);
  }, []);

  const value = useMemo(() => ({ register, unregister }), [register, unregister]);

  return <StatusBarContext.Provider value={value}>{children}</StatusBarContext.Provider>;
}
